import { ComponentType, ReactNode } from 'react'

export type MemberfulLinkProps<C extends ComponentType<any>> = {
  Component: C
  href: string
  children: ReactNode
  onPress?: () => void
} & Omit<React.ComponentProps<C>, 'href' | 'onPress' | 'children'>

/**
 * Web version of MemberfulLink component
 *
 * On web, Memberful links open in a new tab. For checkout/register flows,
 * we append return_to so Memberful comes back to this environment.
 */
export function MemberfulLink<C extends ComponentType<any>>({
  Component,
  href,
  children,
  onPress,
  ...componentProps
}: MemberfulLinkProps<C>) {
  const buildReturnToUrl = () => {
    if (typeof window === 'undefined') {
      return null
    }

    const currentPath = `${window.location.pathname}${window.location.search}`
    const blockedPrefixes = [
      '/api/auth',
      '/app-login',
      '/auth-success',
      '/memberful-checkout',
      '/memberful-checkout-return',
      '/perks',
    ]
    const shouldFallback = blockedPrefixes.some((prefix) =>
      currentPath.startsWith(prefix)
    )
    const returnPath = shouldFallback ? '/' : currentPath || '/'

    return `${window.location.origin}/api/auth/login?returnPath=${encodeURIComponent(
      returnPath
    )}`
  }

  const addReturnToParam = (targetUrl: string) => {
    try {
      const url = new URL(targetUrl)
      if (url.searchParams.has('return_to')) {
        return targetUrl
      }
      const returnTo = buildReturnToUrl()
      if (!returnTo) {
        return targetUrl
      }
      url.searchParams.set('return_to', returnTo)
      return url.toString()
    } catch {
      return targetUrl
    }
  }

  const handlePress = (e?: any) => {
    // Prevent default if event exists
    if (e?.preventDefault) {
      e.preventDefault()
    }

    const isMemberfulUrl = href.includes('memberful.com')
    const isPurchaseFlow =
      href.includes('/checkout') ||
      href.includes('/gift') ||
      href.includes('/register')

    // Call custom onPress handler
    onPress?.()

    // Open in new tab
    if (typeof window !== 'undefined') {
      const urlToOpen =
        isMemberfulUrl && isPurchaseFlow ? addReturnToParam(href) : href
      window.open(urlToOpen, '_blank')
    }
  }

  // Render the component with the custom press handler
  return (
    <Component {...componentProps} onPress={handlePress}>
      {children}
    </Component>
  )
}
